import { z } from 'zod';
/**
 * Avaliação física por dobras cutâneas (P1 do personal). Equações conferidas em 24/10/2026:
 * Jackson & Pollock (1978, homens; Jackson, Pollock e Ward 1980, mulheres), Guedes (jovens
 * universitários) e Petroski (1995, adultos do sul do Brasil); gordura pela equação de Siri
 * (1961). Regra do estudo: só se compara avaliação feita com o mesmo protocolo.
 */
export const SKINFOLDS = [
    'peitoral',
    'axilar',
    'triceps',
    'subescapular',
    'abdominal',
    'suprailiaca',
    'coxa',
    'panturrilha',
];
export const SKINFOLD_LABEL = {
    peitoral: 'Peitoral',
    axilar: 'Axilar média',
    triceps: 'Tríceps',
    subescapular: 'Subescapular',
    abdominal: 'Abdominal',
    suprailiaca: 'Supra-ilíaca',
    coxa: 'Coxa',
    panturrilha: 'Panturrilha medial',
};
export const SKINFOLD_PROTOCOLS = ['jp3', 'jp7', 'guedes', 'petroski'];
export const SKINFOLD_PROTOCOL_INFO = {
    jp3: {
        label: 'Jackson & Pollock, 3 dobras',
        short: 'JP3',
        population: 'Adultos de 18 a 61 anos; o mais usado em academia.',
        folds: { m: ['peitoral', 'abdominal', 'coxa'], f: ['triceps', 'suprailiaca', 'coxa'] },
    },
    jp7: {
        label: 'Jackson & Pollock, 7 dobras',
        short: 'JP7',
        population: 'Adultos de 18 a 61 anos; mais completo, leva mais tempo.',
        folds: {
            m: ['peitoral', 'axilar', 'triceps', 'subescapular', 'abdominal', 'suprailiaca', 'coxa'],
            f: ['peitoral', 'axilar', 'triceps', 'subescapular', 'abdominal', 'suprailiaca', 'coxa'],
        },
    },
    guedes: {
        label: 'Guedes, 3 dobras',
        short: 'Guedes',
        population: 'Jovens universitários brasileiros (17 a 30 anos).',
        folds: {
            m: ['triceps', 'suprailiaca', 'abdominal'],
            f: ['coxa', 'suprailiaca', 'subescapular'],
        },
    },
    petroski: {
        label: 'Petroski, 4 dobras',
        short: 'Petroski',
        population: 'Adultos do sul do Brasil (homens 18 a 61, mulheres 18 a 51 anos).',
        folds: {
            m: ['subescapular', 'triceps', 'suprailiaca', 'panturrilha'],
            f: ['axilar', 'suprailiaca', 'coxa', 'panturrilha'],
        },
    },
};
/** As dobras que o protocolo pede, para o sexo. */
export const protocolFolds = (protocol, sex) => SKINFOLD_PROTOCOL_INFO[protocol].folds[sex];
/** Precisa da idade (todas menos Guedes). */
export const protocolNeedsAge = (protocol) => protocol !== 'guedes';
/**
 * Densidade corporal (g/ml). Nulo se falta dobra do protocolo ou a idade que ele pede.
 * `age` em anos completos no dia da avaliação.
 */
export function bodyDensity(protocol, sex, folds, age) {
    const sites = protocolFolds(protocol, sex);
    if (sites.some((site) => typeof folds[site] !== 'number'))
        return null;
    if (protocolNeedsAge(protocol) && (age === null || age < 0))
        return null;
    const sum = sites.reduce((total, site) => total + folds[site], 0);
    const years = age ?? 0;
    switch (protocol) {
        case 'jp3':
            return sex === 'm'
                ? 1.10938 - 0.0008267 * sum + 0.0000016 * sum ** 2 - 0.0002574 * years
                : 1.0994921 - 0.0009929 * sum + 0.0000023 * sum ** 2 - 0.0001392 * years;
        case 'jp7':
            return sex === 'm'
                ? 1.112 - 0.00043499 * sum + 0.00000055 * sum ** 2 - 0.00028826 * years
                : 1.097 - 0.00046971 * sum + 0.00000056 * sum ** 2 - 0.00012828 * years;
        case 'guedes':
            return sex === 'm' ? 1.1714 - 0.0671 * Math.log10(sum) : 1.1665 - 0.0706 * Math.log10(sum);
        case 'petroski':
            return sex === 'm'
                ? 1.10726863 - 0.00081201 * sum + 0.00000212 * sum ** 2 - 0.00041761 * years
                : 1.1954713 - 0.07513507 * Math.log10(sum) - 0.00041072 * years;
    }
}
/** Siri (1961): % de gordura pela densidade. */
export const siriFatPercent = (density) => 495 / density - 450;
/** Idade em anos completos num dia (AAAA-MM-DD). */
export function ageOn(birth, day) {
    if (!birth)
        return null;
    const [by, bm, bd] = birth.split('-').map(Number);
    const [y, m, d] = day.split('-').map(Number);
    return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}
export const GIRTHS = [
    'braco',
    'bracoContraido',
    'antebraco',
    'torax',
    'cintura',
    'abdomen',
    'quadril',
    'coxa',
    'panturrilha',
];
export const GIRTH_LABEL = {
    braco: 'Braço relaxado',
    bracoContraido: 'Braço contraído',
    antebraco: 'Antebraço',
    torax: 'Tórax',
    cintura: 'Cintura',
    abdomen: 'Abdômen',
    quadril: 'Quadril',
    coxa: 'Coxa',
    panturrilha: 'Panturrilha',
};
const mm = z.number().min(1).max(80);
const cm = z.number().min(10).max(250);
export const skinfoldAssessmentInputSchema = z.object({
    protocol: z.enum(SKINFOLD_PROTOCOLS),
    sex: z.enum(['f', 'm']),
    /** Dia da avaliação (AAAA-MM-DD). */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    weightKg: z.number().min(20).max(400).nullable(),
    heightCm: z.number().min(100).max(250).nullable(),
    folds: z.partialRecord(z.enum(SKINFOLDS), mm),
    girths: z.partialRecord(z.enum(GIRTHS), cm),
    note: z.string().max(400).default(''),
});
const round = (value, places = 1) => Math.round(value * 10 ** places) / 10 ** places;
/** Calcula a avaliação: densidade, % de gordura, massa gorda e magra, IMC e cintura/quadril. */
export function computeSkinfoldAssessment(input, age) {
    const density = bodyDensity(input.protocol, input.sex, input.folds, age);
    if (density === null || density <= 0.9 || density >= 1.2)
        return null;
    const fatPercent = round(siriFatPercent(density));
    const sites = protocolFolds(input.protocol, input.sex);
    const result = {
        protocol: input.protocol,
        sex: input.sex,
        age,
        folds: Object.fromEntries(sites.map((site) => [site, input.folds[site]])),
        foldSum: round(sites.reduce((total, site) => total + input.folds[site], 0)),
        girths: input.girths,
        density: round(density, 4),
        fatPercent,
    };
    if (input.weightKg !== null) {
        result.weightKg = input.weightKg;
        result.fatKg = round((input.weightKg * fatPercent) / 100);
        result.leanKg = round(input.weightKg - result.fatKg);
    }
    if (input.heightCm !== null)
        result.heightCm = input.heightCm;
    if (input.weightKg !== null && input.heightCm !== null)
        result.bmi = round(input.weightKg / (input.heightCm / 100) ** 2);
    const { cintura, quadril, braco, coxa } = input.girths;
    if (cintura !== undefined)
        result.waistCm = cintura;
    if (quadril !== undefined)
        result.hipCm = quadril;
    if (braco !== undefined)
        result.armCm = braco;
    if (coxa !== undefined)
        result.thighCm = coxa;
    if (cintura !== undefined && quadril !== undefined)
        result.waistHip = round(cintura / quadril, 2);
    if (input.note.trim())
        result.note = input.note.trim();
    return result;
}
/**
 * A avaliação anterior que pode ser comparada com esta: o mesmo protocolo, sempre. Dobras de
 * protocolos diferentes dão números que não conversam (regra do estudo).
 */
export function comparableBefore(rows, current) {
    const protocol = current.data.protocol;
    if (typeof protocol !== 'string')
        return null;
    return (rows
        .filter((row) => row !== current &&
        row.data.protocol === protocol &&
        row.data.sex === current.data.sex &&
        row.measuredAt < current.measuredAt)
        .sort((a, b) => b.measuredAt.localeCompare(a.measuredAt))[0] ?? null);
}
// ── Fotos da avaliação ──────────────────────────────────────────────────────
/** As três poses de sempre, para a comparação ser da mesma posição. */
export const ASSESSMENT_POSES = ['frente', 'lado', 'costas'];
export const ASSESSMENT_POSE_LABEL = {
    frente: 'Frente',
    lado: 'Lado',
    costas: 'Costas',
};
export const assessmentPhotoSchema = z.object({
    id: z.uuid(),
    assessmentId: z.uuid(),
    pose: z.enum(ASSESSMENT_POSES),
    /** O dia da avaliação (para comparar por data). */
    measuredAt: z.iso.datetime(),
});
export const addAssessmentPhotoSchema = z.object({
    pose: z.enum(ASSESSMENT_POSES),
    photo: z.object({
        mime: z.enum(['image/jpeg', 'image/webp']),
        /** Base64, já reduzida no aparelho (até ~900 KB). */
        data: z.string().min(16).max(1_300_000),
    }),
});
